import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import type { GnbPanelOptions } from './usePanelScopeRegistry';

/**
 * 패널 위치 타입
 */
export type PanelPreferencePosition = 'bottom' | 'right';

/**
 * 패널 환경설정
 */
export interface PanelPreference {
  /** 패널 활성화 여부 */
  enabled: boolean;
  /** 패널 위치 */
  position: PanelPreferencePosition;
  /** 패널 크기 (px) */
  size: number;
}

/**
 * 레이아웃 타입
 */
export type LayoutPreferenceType = 'list' | 'detail';

/**
 * 레이아웃 타입별 기본 설정
 */
const DEFAULT_PREFERENCES: Record<LayoutPreferenceType, PanelPreference> = {
  list: { position: 'bottom', size: 480, enabled: true },
  detail: { position: 'right', size: 800, enabled: true },
};

/**
 * GnbPanelOptions size를 px 값으로 변환
 */
const sizeToPixels = (size: GnbPanelOptions['size'], position: PanelPreferencePosition): number => {
  const sizeMap: Record<GnbPanelOptions['size'], { bottom: number; right: number }> = {
    s: { bottom: 320, right: 480 },
    m: { bottom: 480, right: 640 },
    l: { bottom: 640, right: 800 },
  };
  return sizeMap[size][position];
};

interface PanelPreferenceState {
  availability: Record<LayoutPreferenceType, boolean>;
  preferences: Record<LayoutPreferenceType, PanelPreference>;
}

interface PanelPreferenceActions {
  /**
   * 레이아웃 타입별 환경설정 조회
   */
  getPreference: (layoutType: LayoutPreferenceType) => PanelPreference;

  /**
   * Registry 기본 옵션으로 초기화 (아직 설정이 없는 경우에만)
   */
  initializeFromRegistry: (layoutType: LayoutPreferenceType, options: GnbPanelOptions) => void;

  /**
   * 레이아웃 타입별 환경설정 초기화
   */
  resetPreference: (layoutType: LayoutPreferenceType) => void;

  /**
   * 레이아웃 타입별 패널 사용 가능 여부 업데이트
   */
  updateAvailability: (layoutType: LayoutPreferenceType, available: boolean) => void;

  /**
   * 레이아웃 타입별 환경설정 업데이트
   */
  updatePreference: (layoutType: LayoutPreferenceType, patch: Partial<PanelPreference>) => void;
}

export type PanelPreferenceStore = PanelPreferenceState & PanelPreferenceActions;

const STORAGE_KEY = 'kc-panel-settings-v2';
const STORE_NAME = 'PanelPreferenceStore';

/**
 * 패널 환경설정 스토어
 *
 * - 레이아웃 타입(list/detail) 단위로 패널 설정 관리
 * - 로컬스토리지 `kc-panel-settings-v2`에 영속화
 * - Registry `defaultGnbOptions`를 기본값으로 사용 가능
 *
 * @example
 * ```tsx
 * const { getPreference, updatePreference } = usePanelPreferenceStore();
 *
 * // 설정 조회
 * const listPref = getPreference('list');
 *
 * // 설정 업데이트
 * updatePreference('list', { size: 500 });
 * ```
 */
export const usePanelPreferenceStore = create<PanelPreferenceStore>()(
  devtools(
    persist(
      (set, get) => ({
        preferences: {
          list: DEFAULT_PREFERENCES.list,
          detail: DEFAULT_PREFERENCES.detail,
        },
        availability: { list: true, detail: true },

        getPreference: layoutType => get().preferences[layoutType],

        updatePreference: (layoutType, patch) => {
          set(
            state => ({
              preferences: {
                ...state.preferences,
                [layoutType]: { ...state.preferences[layoutType], ...patch },
              },
            }),
            undefined,
            `${STORE_NAME}/update/${layoutType}`,
          );
        },

        resetPreference: layoutType => {
          set(
            state => ({
              preferences: {
                ...state.preferences,
                [layoutType]: DEFAULT_PREFERENCES[layoutType],
              },
            }),
            undefined,
            `${STORE_NAME}/reset/${layoutType}`,
          );
        },

        updateAvailability: (layoutType, available) => {
          set(
            state => ({
              availability: { ...state.availability, [layoutType]: available },
            }),
            undefined,
            `${STORE_NAME}/availability/${layoutType}`,
          );
        },

        initializeFromRegistry: (layoutType, options) => {
          const current = get().preferences[layoutType];
          const defaultPref = DEFAULT_PREFERENCES[layoutType];

          // 현재 설정이 기본값과 동일한 경우에만 Registry 값으로 초기화
          if (current.position === defaultPref.position && current.size === defaultPref.size) {
            const position = options.position;
            const size = sizeToPixels(options.size, position);

            set(
              state => ({
                preferences: {
                  ...state.preferences,
                  [layoutType]: {
                    ...state.preferences[layoutType],
                    position,
                    size,
                  },
                },
              }),
              undefined,
              `${STORE_NAME}/initFromRegistry/${layoutType}`,
            );
          }
        },
      }),
      {
        name: STORAGE_KEY,
        partialize: state => ({ preferences: state.preferences }),
      },
    ),
    { name: STORE_NAME },
  ),
);

/**
 * 패널 환경설정 기본값
 */
export { DEFAULT_PREFERENCES };
